import {
  PaidProvisionMonthResponseDto,
  ProvisionListResponseDto,
  ProvisionStepResponseDto,
  ProvisionStepsListResponseDto,
} from "@/lib/interfaces/bonus";
import { UserSelectableEntityResponseDto } from "@/lib/interfaces/user";
import { Paginated } from "@/lib/interfaces/utils";
import {  User } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import useApiRequest from "../../../lib/hooks/useRequest";
import { formatDate, formatDateDetail } from "../../../lib/hooks/utils";
import DataFetchParent from "../../../ui/components/ui/data-fetch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/components/ui/select";
import { StarsRating } from "../../../ui/components/ui/stars-rating";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/components/ui/tooltip";
import UserProfileRow from "./user-profile-row";
import StarIcon from "../../../ui/components/icons/star-icon";
import RocketIcon from "../../../ui/components/icons/rocket-icon";
import ContractIcon from "../../../ui/components/icons/contract-icon";
import BankIcon from "../../../ui/components/icons/bank-icon";

type CustomerOverviewData = {
  list: ProvisionListResponseDto[];
  provisionSteps: ProvisionStepResponseDto[];
};

const CustomerOverview = ({ isAdmin }: { isAdmin: boolean }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CustomerOverviewData>({
    list: [],
    provisionSteps: [],
  });
  const [selectedUserId, setSelectedUserId] = useState<string>("none");
  const { apiRequest } = useApiRequest();

  const getCustomerOverviewData = useCallback(async () => {
    if (isAdmin && selectedUserId === "none") {
      setData({ list: [], provisionSteps: [] });
      setLoading(false);
      return;
    }

    setLoading(true);
    let url = `provision/${isAdmin ? "admin" : "employee"}/provision-list`;
    if (isAdmin && selectedUserId !== "none") {
      url += `/${selectedUserId}`;
    }
    const provisionListResponse = await apiRequest<Paginated<ProvisionListResponseDto>>(url, "GET");
    const provisionStepsResponse = await apiRequest<ProvisionStepsListResponseDto>(
      `provision/${isAdmin ? "admin" : "employee"}/provision-steps`,
      "GET"
    );
    if (provisionListResponse.data && provisionStepsResponse.data) {
      setData({ list: provisionListResponse.data.docs, provisionSteps: provisionStepsResponse.data.steps });
    }
    setLoading(false);
  }, [apiRequest, isAdmin, selectedUserId]);

  useEffect(() => {
    getCustomerOverviewData();
  }, [getCustomerOverviewData, selectedUserId]);

  return (
    <div className="w-full bg-white rounded-lg p-4 flex flex-col gap-2">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h5 className="font-semibold">Kunden Übersicht</h5>
          {isAdmin ? (
            <CustomerOverviewAdminHeader selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} />
          ) : null}
        </div>
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: CustomerOverviewData) => {
            return (
              <div className="w-full">
                {data.list.length === 0 ? (
                  <>
                    {isAdmin && selectedUserId === "none" ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <User size={32} className="text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Kein Mitarbeiter ausgewählt</h3>
                        <p className="text-gray-500 max-w-sm">
                          Bitte wähle einen Mitarbeiter aus, um die Kundenübersicht anzuzeigen.
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <User size={32} className="text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Kunden verfügbar</h3>
                        <p className="text-gray-500 max-w-sm">
                          Es wurden noch keine Kunden für diesen Mitarbeiter gefunden.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <ProvisionList data={data.list} provisionSteps={data.provisionSteps} />
                )}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

const ProvisionList = ({
  data,
  provisionSteps,
}: {
  data: ProvisionListResponseDto[];
  provisionSteps: ProvisionStepResponseDto[];
}) => {
  return (
    <>
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-4 p-4 border-b">
        <div className="col-span-3 font-semibold text-[#12282A] text-sm">Kunden</div>
        <div className="col-span-5 font-semibold text-[#12282A] text-sm">Übersicht</div>
        <div className="col-span-1 font-semibold text-[#12282A] text-sm">Gesamt</div>
        <div className="col-span-1 font-semibold text-[#12282A] text-sm">Bewertung</div>
        <div className="col-span-2 font-semibold text-[#12282A] text-sm">Status</div>
      </div>

      {/* Table Body */}
      <div className="flex flex-col">
        {data.map((customer) => (
          <CustomerRow key={customer._id} customer={customer} provisionSteps={provisionSteps} />
        ))}
      </div>
    </>
  );
};

const CustomerOverviewAdminHeader = ({
  selectedUserId,
  setSelectedUserId,
}: {
  selectedUserId: string;
  setSelectedUserId: (value: string) => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [users, setUsers] = useState<UserSelectableEntityResponseDto[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const getUsers = useCallback(async () => {
    const usersResponse = await apiRequest<UserSelectableEntityResponseDto[]>(
      "provision/admin/user-selectable-entities",
      "GET"
    );
    if (usersResponse.data) {
      setUsers(usersResponse.data);
    }
    setLoadingUsers(false);
  }, [apiRequest]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  return (
    <DataFetchParent
      isLoading={loadingUsers}
      data={users}
      renderElement={(data: UserSelectableEntityResponseDto[]) => {
        return (
          <Select
            onValueChange={(value) => {
              setSelectedUserId(value);
            }}
            defaultValue={selectedUserId}
          >
            <SelectTrigger className={`bg-white h-10 w-fit`}>
              <SelectValue placeholder="Mitarbeiter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keiner ausgewählt</SelectItem>
              {data.map((user) => (
                <SelectItem key={user._id} value={user._id}>
                  {user.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }}
    />
  );
};

interface CustomerRowProps {
  customer: ProvisionListResponseDto;
  provisionSteps: ProvisionStepResponseDto[];
}

const CustomerRow: React.FC<CustomerRowProps> = ({ customer, provisionSteps }) => {
  let icon = null;

  switch (customer.statusIcon) {
    case "stars":
      icon = <StarIcon />;
      break;
    case "rocket":
      icon = <RocketIcon />;
      break;
    case "contract":
      icon = <ContractIcon />;
      break;
    case "bank":
      icon = <BankIcon />;
      break;
    default:
      icon = <ContractIcon />;
      break;
  }
  const colorStyle = customer.statusColor
    ? {
        borderColor: customer.statusColor,
        color: customer.statusColor,
        backgroundColor: `${customer.statusColor}33`, // Hex with 20% opacity
      }
    : {};

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`hover:bg-gray-50 transition-colors cursor-pointer`}>
            <div className="grid grid-cols-12 gap-4 p-2 items-center">
              {/* Customer Info */}
              <div className="col-span-3 flex gap-2 items-center">
                <UserProfileRow profilePictureUrl={customer.profilePictureUrl} name={customer.name} email={""} highlight={customer.loopState} />
              </div>

              {/* Overview */}
              <div className="col-span-5">
                <div className="text-[16px] font-semibold text-[#12282A]">
                  <OverviewProgress
                    progress={customer.months}
                    paidProvisions={customer.paidProvisions}
                    provisionSteps={provisionSteps}
                    loopState={customer.loopState}
                  />
                </div>
              </div>

              {/* Total */}
              <div className="col-span-1">
                <div className="text-[16px] text-[#12282A] font-semibold">{customer.provisionAmount} €</div>
                <div className="text-[12px] text-[#888]">Monate: {customer.months}</div>
              </div>

              {/* Rating */}
              <div className="col-span-1">
                <div className="flex justify-start items-center gap-2 text-sm text-[#D0D5DD]">
                  <StarsRating starAmount={customer.rating ?? 0} />
                </div>
              </div>

              {/* Status */}
              <div className="col-span-2">
                <div
                  className={`flex justify-center border bg-opacity-5 items-center gap-2 text-xs font-bold rounded-2xl px-1 py-1 w-full`}
                  style={colorStyle}
                >
                  {icon}
                  <span>{customer.statusValue}{customer.statusDate ? ` - ${formatDateDetail(customer.statusDate)}` : ""}</span>
                </div>
              </div>
            </div>
            <hr className="border-gray-200" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          <div className="space-y-2">
            <div className="font-semibold">{customer.name}</div>
            <div className="text-xs space-y-1">
              <div>
                <span className="font-medium">Startdatum:</span> {formatDate(customer.startDate)}
              </div>
              {customer.paidProvisions.length > 0 && (
                <div>
                  <div className="font-medium">Provisionen:</div>
                  <div className="ml-2 space-y-0.5">
                    {customer.paidProvisions.map((provision, index) => (
                      <div key={index} className="text-xs">
                        Monat {provision.months}: {provision.amount}€ ({formatDate(provision.provisionDate)})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const OverviewProgress = ({
  progress,
  paidProvisions,
  provisionSteps,
  loopState,
}: {
  progress: number;
  paidProvisions: PaidProvisionMonthResponseDto[];
  provisionSteps: ProvisionStepResponseDto[];
  loopState: boolean;
}) => {
  const totalSegments = 12;

  // Use golden gradient when progress is complete (12), otherwise use the regular gradient
  const gradientStyle = loopState
  ? {
      background: "linear-gradient(90deg, #EBD197 0%, #C49F42 50%, #EBD197 100%)",
      backgroundSize: "15% 100%",
      backgroundRepeat: "repeat"
    }
  : {
      background: "linear-gradient(90deg, #C0DE60 0%, #5FD5DE 50%, #C0DE60 100%)",
      backgroundSize: "75% 100%",
      backgroundRepeat: "repeat"
    };

  const renderSegment = (index: number) => {
    const segmentNumber = index + 1;
    let isFilled = segmentNumber <= progress;

    let bonusInfo = null;
    const hasPaidProvisionThisSegment = paidProvisions.find((provision) => provision.months === segmentNumber);
    if (hasPaidProvisionThisSegment) {
      // Only show paid provisions if they exist
      bonusInfo = paidProvisions.find((provision) => provision.months === segmentNumber);
    } else {
      // No paid provisions exist, so use provision steps
      if (segmentNumber > progress) {
        bonusInfo = provisionSteps.find((bonus) => bonus.months === segmentNumber);
      }
    }

    if (bonusInfo) {
      const marginClass = segmentNumber === 12 ? "-mx-1" : "-mx-4";
      isFilled = paidProvisions.find((provision) => provision.months === segmentNumber) !== undefined;
      // Bonus milestone segment
      return (
        <div
          key={index}
          className={`z-10 flex items-center ${marginClass} py-1 justify-center flex-1 rounded-lg [-webkit-mask-image:linear-gradient(black,black)] [mask-image:linear-gradient(black,black)]`}
        >
          <div style={isFilled ? gradientStyle : {}} className={`absolute inset-0 w-full ${isFilled ? "" : "bg-gray-200"}`}></div>
          <p className={`z-10 text-xs ${isFilled ? "text-white" : "text-gray-400"}`}>
            {`+ ${bonusInfo.amount}€`}
          </p>
        </div>
      );
    } else {
      // Regular segment
      return (
        <div
          key={index}
          className={`h-2 flex-1 rounded-sm [-webkit-mask-image:linear-gradient(black,black)] [mask-image:linear-gradient(black,black)]`}
        >
          <div style={isFilled ? gradientStyle : {}} className={`h-2 absolute left-0 w-full ${isFilled ? "" : "bg-gray-200"}`}></div>
        </div>
      );
    }
  };

  return (
    <div className="relative flex gap-px items-center p-1">
      {Array.from({ length: totalSegments }, (_, index) => renderSegment(index))}
    </div>
  );
};

export default CustomerOverview;